import * as React from "react";
import _ from "lodash";

import {
  FocusContext,
  useFocusable,
  setFocus,
} from "@noriginmedia/norigin-spatial-navigation";
import { MoviesSlider } from "./moviesList";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";
import SearchFilters from "./searchFilters";
import useMockInput from "./input.mock";
import toast from "react-hot-toast";
import { Box, Typography } from "../legacy-ui/materialCompat";
import { useDebounceValue } from "../../utils/hooks/useDebounceValue";
import { getAgeRangePublicRoute } from "../../utils/ageRange.util";
import smoothScroll from "../../utils/animateScroll";
import {
  getPaginationFocusEdge,
  getVerticalFocusScrollTarget,
} from "../../utils/focusScroll";
import { useInputMode } from "../../utils/hooks/useInputMode";
import { trackGoogleAnalyticsEvent } from "../../services/googleAnalytics";

const keyboardKeys = [
  [
    "ا", "ب", "پ", "ت", "ث", "ج", "چ", "ح", "خ", "د",
    "ذ", "ر", "ز", "ژ", "س", "ش", "ص", "ض", "ط", "ظ",
    "ع", "غ", "ف", "ق", "ک", "گ", "ل", "م", "ن", "و", "ه", "ی",
  ],
  [
    "a", "b", "c", "d", "e", "f", "g", "h", "i", "j",
    "k", "l", "m", "n", "o", "p", "q", "r", "s", "t",
    "u", "v", "w", "x", "y", "z", "del", "space", "caps",
  ],
  ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"],
];

const KeyItem = ({ char, isEng, isCaps, onEnterPress }) => {
  const { ref, focused, focusSelf } = useFocusable({
    onEnterPress,
  });
  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = React.useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const controllsGuide = {
    right: (
      <img
        src={"/images/arrow_right.svg"}
        width={64}
        height={64}
        alt={"To The right"}
      />
    ),
    left: (
      <img
        src={"/images/arrow_left.svg"}
        width={64}
        height={64}
        alt={"To The left"}
      />
    ),
    del: (
      <img
        src={"/images/del_button.svg"}
        width={38}
        height={29.25}
        alt={"Delete"}
        className="min-w-5 z-10"
      />
    ),
    sub: (
      <img
        src={"/images/sub_button.svg"}
        width={38}
        height={38}
        alt={"Submit"}
      />
    ),
    caps: isCaps ? (
      <img
        src={"/images/caps_filled.svg"}
        width={38}
        height={38}
        alt={"Caps On"}
        className="min-w-5 z-10"
      />
    ) : (
      <img
        src={"/images/caps.svg"}
        width={38}
        height={38}
        alt={"Caps Off"}
        className="min-w-5 z-10"
      />
    ),
    space: (
      <img
        src={"/images/space.svg"}
        alt="Space"
        width={38}
        className="min-w-5 z-10"
      />
    ),
  };

  return (
    <div
      ref={ref}
      onClick={onEnterPress}
      onMouseEnter={handleMouseEnter}
      className={"legacy-search-key" + (focused ? " is-focused" : "")}
    >
      <div className="legacy-search-key-border" />
      {controllsGuide[char] ? (
        controllsGuide[char]
      ) : (
        <span className={"legacy-search-key-text" + (isEng ? " is-eng" : "")}>
          {isCaps && !controllsGuide[char] ? char.toUpperCase() : char}
        </span>
      )}
    </div>
  );
};

const SearchKeyboard = ({
  submitSearch,
  addCharacter,
  removeCharacter,
  moveCursor,
}) => {
  const [isCaps, setCaps] = React.useState(false);
  const { ref, focusKey } = useFocusable({});

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-search-keyboard" ref={ref}>
        {keyboardKeys?.map((item, index) => (
          <div className="legacy-search-keyboard-row" key={"row_" + index}>
            {item?.map((keyItem, keyIndex) => (
              <KeyItem
                char={keyItem}
                isCaps={isCaps}
                isEng={index === 1}
                key={"row_" + index + "_key_" + keyIndex}
                onEnterPress={() => {
                  if (keyItem === "space") {
                    addCharacter(" ");
                  } else if (keyItem === "right") {
                    moveCursor("left");
                  } else if (keyItem === "del") {
                    removeCharacter();
                  } else if (keyItem === "sub") {
                    submitSearch();
                  } else if (keyItem === "caps") {
                    setCaps(!isCaps);
                  } else {
                    addCharacter(
                      isCaps ? keyItem.toLocaleUpperCase() : keyItem
                    );
                  }
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </FocusContext.Provider>
  );
};

const PAGE_SIZE = 30;
const SEARCH_INPUT_FOCUS_KEY = "search-input";

const SearchPageContent = () => {
  const ageRange = getAgeRangePublicRoute();
  const { ref, focusKey } = useFocusable();
  const [searchResult, setSearchResult] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [hasMore, setHasMore] = React.useState(false);
  const [filters, setFilters] = React.useState({
    category: null,
    genre: null,
    country: null,
    kind: null,
    contentAgeRange: null,
    sortingType: null,
  });
  const [mountReset, setMountReset] = React.useState(false);
  const initRef = React.useRef(false);
  const scrollRef = React.useRef();
  const sentinelRef = React.useRef(null);

  const { InputComponent, moveCursor, addCharacter, removeCharacter, text } =
    useMockInput({
      initRef,
      isLoading,
      inputFocusKey: SEARCH_INPUT_FOCUS_KEY,
    });

  const [search, setSearch] = useDebounceValue(text, 1000);

  const submitSearch = async (nextPage = 1) => {
    if (isLoading) return;

    setIsLoading(true);

    const { category, genre, kind } = filters;
    const params = { pageNumber: nextPage, pageIndex: PAGE_SIZE, contentAgeRange: ageRange };
    if (text) params.title = text;
    if (category) params.category = category;
    if (genre) params.genre = genre;
    if (kind && kind !== "isTicketing" && kind !== "live") params.kind = kind;
    if (kind && kind === "isTicketing") params.isTicketing = true;
    if (kind && kind === "live") params.isLive = true;

    try {
      const response = await httpService.get(getCMSApiRoute("content"), {
        params,
      });

      if (response.status === 200) {
        const { data } = response.data;
        const newResults = data.results || [];

        if (nextPage === 1) {
          setSearchResult(newResults);
        } else {
          setSearchResult((prev) => _.uniqBy([...prev, ...newResults], (x) => x._id));
        }

        setPage(nextPage);
        setHasMore(data.total > nextPage * PAGE_SIZE);

        if (nextPage === 1) {
          var analyticsPayload = {
            search_term: text || "",
            results_count: data.total || newResults.length || 0,
            filter_category: category || "",
            filter_genre: genre || "",
            filter_kind: kind || "",
          };
          trackGoogleAnalyticsEvent("search", analyticsPayload);
          trackGoogleAnalyticsEvent("search_performed", analyticsPayload);
        }
      } else {
        toast.error("خطا در دریافت اطلاعات");
      }
    } catch (error) {
      Object.keys(error).map((item) => console.log(item));
      toast.error("خطا در دریافت اطلاعات");
    } finally {
      setIsDirty(true);
      setIsLoading(false);
    }
  };

  const loadMoreRef = React.useRef(null);
  loadMoreRef.current = () => {
    if (hasMore && !isLoading) {
      submitSearch(page + 1);
    }
  };

  const resetFilters = () => {
    setFilters({
      category: null,
      genre: null,
      country: null,
      kind: null,
      contentAgeRange: null,
      sortingType: null,
    });

    setPage(1);
    setHasMore(false);

    if (text?.length > 2) {
      submitSearch(1);
    } else {
      setSearchResult([]);
      setIsDirty(false);
    }
  };

  const { isMouseFocus } = useInputMode();
  const onRowFocus = React.useCallback(
    (props) => {
      if (isMouseFocus) return;
      const targetScroll = getVerticalFocusScrollTarget(scrollRef.current, props || {}, {
        topPadding: 96,
        bottomPadding: 128,
      });
      smoothScroll(scrollRef, targetScroll, 500);

      // Pagination trigger via spatial navigation
      if (scrollRef.current && props) {
        if (getPaginationFocusEdge(scrollRef.current, props, 160) > 0) {
          loadMoreRef.current?.();
        }
      }
    },
    [scrollRef, isMouseFocus]
  );

  // Primary: IntersectionObserver sentinel with 10vh rootMargin
  React.useEffect(() => {
    const scrollContainer = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!scrollContainer || !sentinel) return;

    const marginPx = Math.round(window.innerHeight * 0.1);
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreRef.current?.();
        }
      },
      { root: scrollContainer, rootMargin: `0px 0px ${marginPx}px 0px`, threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => {
      observer.unobserve(sentinel);
    };
  }, [searchResult.length]);

  // Backup: scroll position check
  React.useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let scrollTimeout = null;
    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        loadMoreRef.current?.();
      }, 100);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [searchResult.length]);

  React.useEffect(() => {
    if (filters.category || filters.genre || filters.country || filters.kind) {
      setPage(1);
      setHasMore(false);
      submitSearch(1);
      setMountReset(true);
    } else {
      setTimeout(() => {
        setMountReset(false);
      }, 300);
    }
  }, [filters]);

  React.useEffect(() => {
    setSearch(text);
  }, [text]);

  React.useEffect(() => {
    if (search.length >= 3) {
      setPage(1);
      setHasMore(false);
      submitSearch(1);
    }
  }, [search]);

  React.useEffect(() => {
    var delays = [0, 120, 350, 800];
    var timers = delays.map(function (delay) {
      return setTimeout(function () {
        setFocus(SEARCH_INPUT_FOCUS_KEY);
      }, delay);
    });

    return function () {
      timers.forEach(function (timer) {
        clearTimeout(timer);
      });
    };
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="legacy-search-page" ref={ref}>
        <InputComponent />
        <SearchFilters
          onSearch={() => submitSearch()}
          resetFilters={resetFilters}
          mountReset={mountReset}
          filters={filters}
          setFilters={setFilters}
        />
        <SearchKeyboard
          addCharacter={addCharacter}
          removeCharacter={removeCharacter}
          moveCursor={moveCursor}
          submitSearch={() => submitSearch()}
        />
        {searchResult?.length > 0 && (
          <div className="legacy-search-scroll" ref={scrollRef}>
            <MoviesSlider
              movies={searchResult}
              onFocus={onRowFocus}
            />
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 56 }}>
                <span style={{ color: 'white', fontFamily: 'AnjomanMaxFN-Regular' }}>در حال بارگذاری...</span>
              </div>
            )}
          </div>
        )}
        {!isLoading && isDirty && searchResult?.length === 0 && (
          <div className="legacy-search-no-results">
            <span className="legacy-search-no-results-text">محتوایی یافت نشد</span>
          </div>
        )}
      </div>
    </FocusContext.Provider>
  );
};

export default SearchPageContent;
